import { usePopupStore } from '@/stores/PopupStore'
import { clearTokenExpirationCheck } from './TokenManager'
import { useMainStore } from '@/stores/MainStore'

let sessionErrorHandled = false

/**
 * Centralized Error Handler for managing session expiration and critical errors.
 * @param {string} message - The error message to display to the user.
 * @param {boolean} redirect - Whether to redirect the user to the login page.
 * @param {number} redirectDelay - Delay before redirecting (in milliseconds), if 0, user click is required.
 * @param {string} buttonText - Custom text for the button in the popup.
 * @param {boolean} showLogout - Whether to display a "Logout" button in the popup.
 */
export const handleSessionError = (
  message: string = 'Your session has expired. Refreshing login...',
  redirect: boolean = true,
  redirectDelay: number = 0,
  buttonText: string = 'Refresh',
  showLogout: boolean = false
) => {
  if (sessionErrorHandled) {
    console.warn('Session error already handled. Debouncing further actions.')
    return
  }
  sessionErrorHandled = true

  const popupStore = usePopupStore()
  const mainStore = useMainStore()

  // Function to handle the redirection logic
  const performRedirect = async () => {
    sessionErrorHandled = false
    if (redirect) {
      // Logout and clear token expiration warning
      clearTokenExpirationCheck()
      mainStore.removeFocusListener()

      // Redirect directly to Azure Static Web App login
      const appUrl = encodeURIComponent(window.location.href)
      window.location.href = `/.auth/login/aad?post_login_redirect_uri=${appUrl}`
    }
  }

  // Function to handle logout
  const handleLogout = async () => {
    await mainStore.logout() // Ensure this method clears user data
    sessionErrorHandled = false
    const appUrl = import.meta.env.VITE_APP_URL
    window.location.href = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(appUrl)}`
  }

  if (redirectDelay > 0) {
    // Show popup with the error message and auto-dismiss after the delay
    popupStore.show({
      message,
      buttonText,
      onConfirmAction: performRedirect,
      showLogout, // Pass the showLogout flag
      onLogoutAction: handleLogout // Pass the logout action
    })

    setTimeout(() => {
      performRedirect()
    }, redirectDelay)
  } else {
    // Show popup and wait for user confirmation
    popupStore.show({
      message,
      buttonText,
      onConfirmAction: performRedirect,
      showLogout, // Pass the showLogout flag
      onLogoutAction: handleLogout // Pass the logout action
    })
  }
}
